/**
 * IHS-5 Top scorers — per-team scorer leaderboard. Stacked vertically:
 *   - HOME block: hero row (top scorer) + 3-4 list rows
 *   - AWAY block: hero row + 3-4 list rows
 *
 * The hero row uses a stadium-style gradient background; the goal-count
 * box on the right is solid blue (home) or orange (away). List rows
 * are flat with a smaller colored count box on the right.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import type {
  IhsScorerEntry,
  IhsTopScorersData,
  IhsTopScorersSide,
} from '../../../types/ihs.js';
import { IhsCard } from './IhsCard.js';
import { PlayerHeroRow } from '../../primitives/PlayerHeroRow.js';
import { PlayerRow } from '../../primitives/PlayerRow.js';
import { TEAM_HOME_COLOR, TEAM_AWAY_COLOR } from '../../../styles/team-colors.js';
import styles from './IhsTopScorers.module.css';

import { useBrandToken } from '../../../tenant/useBrandToken.js';

const HOME_COLOR = TEAM_HOME_COLOR;
const AWAY_COLOR = TEAM_AWAY_COLOR;

function HeroSlot({
  scorer,
  side,
  crestSrc,
  teamName,
  goalsLabel,
}: {
  scorer: IhsScorerEntry;
  side: 'home' | 'away';
  crestSrc?: string;
  teamName: string;
  goalsLabel: string;
}): ReactElement {
  const boxColor = side === 'home' ? HOME_COLOR : AWAY_COLOR;
  return (
    <PlayerHeroRow
      player={{
        name: scorer.name,
        ...(scorer.position !== null ? { position: scorer.position } : {}),
        ...(scorer.nationalityLabel !== null
          ? { nationalityLabel: scorer.nationalityLabel }
          : {}),
        ...(scorer.nationalityCode !== null
          ? { nationalityCode: scorer.nationalityCode }
          : {}),
      }}
      {...(crestSrc !== undefined ? { teamBadge: crestSrc } : {})}
      teamName={teamName}
      stat={{ value: scorer.goals, label: goalsLabel, color: boxColor }}
    />
  );
}

function ListSlot({
  scorer,
  side,
  crestSrc,
  teamName,
  rank,
  plain = false,
}: {
  scorer: IhsScorerEntry;
  side: 'home' | 'away';
  crestSrc?: string;
  teamName: string;
  /** ADR-063 topscorers-style 'list'/'ledger': leaderboard rank number. */
  rank?: number;
  /** 'ledger' variant: plain tabular numerals instead of coloured chips. */
  plain?: boolean;
}): ReactElement {
  const boxColor = side === 'home' ? HOME_COLOR : AWAY_COLOR;
  return (
    <PlayerRow
      player={{
        name: scorer.name,
        subtitle: scorer.position ?? '',
        ...(scorer.nationalityCode !== null
          ? { nationalityCode: scorer.nationalityCode }
          : {}),
      }}
      {...(crestSrc !== undefined ? { teamBadge: crestSrc } : {})}
      teamName={teamName}
      {...(rank !== undefined ? { rank } : {})}
      stats={[plain ? { value: scorer.goals } : { value: scorer.goals, color: boxColor }]}
    />
  );
}

type TopScorersVariant = 'hero' | 'list' | 'ledger';

function TeamBlock({
  side,
  sideKey,
  goalsLabel,
  variant,
}: {
  side: IhsTopScorersSide;
  sideKey: 'home' | 'away';
  goalsLabel: string;
  /** ADR-063 --topscorers-style: hero (leader card, platform default) |
   *  list (compact ranked list, coloured chips) | ledger (ranked list,
   *  plain tabular numerals). */
  variant: TopScorersVariant;
}): ReactElement {
  const top = side.scorers[0];
  if (side.scorers.length === 0) return <></>;

  if (variant !== 'hero') {
    const all = side.scorers.slice(0, 5);
    return (
      <div className={styles.block}>
        <div className={styles.teamLabel}>{side.team.name}</div>
        <div className={styles.list}>
          {all.map((s, i) => (
            <ListSlot
              key={s.playerId}
              scorer={s}
              side={sideKey}
              {...(side.team.badge !== undefined ? { crestSrc: side.team.badge } : {})}
              teamName={side.team.name}
              rank={i + 1}
              plain={variant === 'ledger'}
            />
          ))}
        </div>
      </div>
    );
  }

  const rest = side.scorers.slice(1, 5);
  return (
    <div className={styles.block}>
      {top ? (
        <HeroSlot
          scorer={top}
          side={sideKey}
          {...(side.team.badge !== undefined ? { crestSrc: side.team.badge } : {})}
          teamName={side.team.name}
          goalsLabel={goalsLabel}
        />
      ) : null}
      {rest.length > 0 && (
        <div className={styles.list}>
          {rest.map((s) => (
            <ListSlot
              key={s.playerId}
              scorer={s}
              side={sideKey}
              {...(side.team.badge !== undefined ? { crestSrc: side.team.badge } : {})}
              teamName={side.team.name}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function IhsTopScorers({
  data,
}: {
  data: IhsTopScorersData;
}): ReactElement {
  const { t } = useTranslation();
  const title =
    data.header?.title ??
    t('ihs.topScorers.title', { defaultValue: 'Top Goalscorers' });
  const goalsLabel = t('ihs.topScorers.goalsLabel', { defaultValue: 'Goals' });

  const hasAny = data.home.scorers.length > 0 || data.away.scorers.length > 0;

  // ADR-063: the brand picks the widget's presentation variant.
  const styleToken = useBrandToken('--topscorers-style');
  const variant: TopScorersVariant =
    styleToken === 'list' || styleToken === 'ledger' ? styleToken : 'hero';

  return (
    <IhsCard title={title}>
      {!hasAny ? (
        <p className={styles.empty}>
          {t('ihs.topScorers.noData', {
            team: data.home.team.name,
            defaultValue: 'No scorers to show',
          })}
        </p>
      ) : (
        <div className={styles.stack}>
          <TeamBlock side={data.home} sideKey="home" goalsLabel={goalsLabel} variant={variant} />
          <TeamBlock side={data.away} sideKey="away" goalsLabel={goalsLabel} variant={variant} />
        </div>
      )}
    </IhsCard>
  );
}
